import type {
  DecorationInstance,
  GameSettings,
  OfflineProgress,
  PlantInstanceId,
  PlantState,
  SaveData,
  Season,
  ToolType,
  MascotCareAction,
} from "../types";
import type { SyncManager } from "./SyncManager";
import type { ApiClient, CredentialStore } from "./ApiClient";
import { AccountModal } from "../ui/AccountModal";
import { advanceGrowthDay, daysToHarvestFrom, estimateDaysToHarvest } from "../entities/PlantGrowth";
import { Camera } from "../engine/Camera";
import { ParticleSystem } from "../engine/ParticleSystem";
import { IsometricRenderer } from "../engine/IsometricRenderer";
import { WindShader } from "../engine/WindShader";
import { GardenGrid } from "../entities/GardenGrid";
import {
  PLANT_DEFINITIONS,
  PLANT_DEFINITION_LIST,
  getPlantDefinition,
} from "../entities/PlantDefinitions";
import { DECORATION_DEFINITIONS } from "../entities/Decoration";
import { TimeManager } from "./TimeManager";
import { WeatherSystem } from "./WeatherSystem";
import { SeasonSystem } from "./SeasonSystem";
import { GardenStorage } from "./Storage";
import { EconomySystem, type ShopItem } from "../systems/EconomySystem";
import { QuestSystem } from "../systems/QuestSystem";
import { AudioSystem } from "../systems/AudioSystem";
import { CareSystem, type CareCommand } from "../systems/CareSystem";
import { PhotoMode } from "../systems/PhotoMode";
import { MascotSystem } from "../systems/MascotSystem";
import { HUD } from "../ui/HUD";
import { Toolbar, type ToolbarTool } from "../ui/Toolbar";
import { DetailModal, HelpModal, MascotModal, OnboardingModal, PhotoModal, QuestModal, SeedDrawerModal, ShopModal, type SeedDrawerItem } from "../ui/Modals";
import { getMascotDefinition } from "../entities/MascotDefinitions";
import { Inspector } from "../ui/Inspector";
import { Toast } from "../ui/Toast";
import { DecorationPanel } from "../ui/DecorationPanel";
const SETTINGS: GameSettings = {
  audio: {
    muted: false,
    masterVolume: 0.8,
    musicVolume: 0.28,
    ambienceVolume: 0.4,
    sfxVolume: 0.75,
  },
  reducedMotion: false,
  highContrast: false,
  daysPerSeason: 10,
  realMinutesPerGameDay: 3,
  particleQuality: "auto",
};
/** 新規プレイ時の庭の広さ。8×8 は初期状態では持て余すため 6×6 から始める。 */
const INITIAL_GARDEN_SIZE = 6;

/** アイソメトリックタイルの基準サイズ。カメラのフィット計算と共有する。 */
const TILE_WIDTH = 160;
const TILE_HEIGHT = 80;

const SEASON_JA: Record<string, string> = { spring: '春', summer: '夏', autumn: '秋', winter: '冬' };
const PLANT_ICONS: Record<string, string> = { tulip: "🌷", sunflower: "🌻", rose: "🌹", lavender: "🪻", tomato: "🍅", carrot: "🥕", cosmos: "🌸", herb: "🌿", hydrangea: "💐", apple_tree: "🍎", viola: "🌼", ornamental_kale: "🥬", spinach: "🥗" };
const SHOP: readonly ShopItem[] = [
  ...PLANT_DEFINITION_LIST.map((p) => ({
    id: `seed-${p.id}`,
    displayName: `${p.displayName}の${p.plantingForm === "sapling" ? "苗木" : p.plantingForm === "seedling" ? "苗" : "種"}`,
    type: "seed" as const,
    price: p.seedPriceGP,
    speciesId: p.id,
    quantity: 3,
    description: `${p.seasonsAvailable.map((s) => SEASON_JA[s] ?? s).join('・')}に植えられる${p.plantingForm === "sapling" ? "苗木" : p.plantingForm === "seedling" ? "苗" : "種"} ×3`,
    emoji: PLANT_ICONS[p.id] ?? (p.category === "vegetable" ? "🥕" : "🌱"),
  })),
  ...Object.values(DECORATION_DEFINITIONS).map((d) => ({
    id: d.id,
    displayName: d.displayName,
    type: "decoration" as const,
    price: d.priceGP,
    catalogId: d.id,
    description: `庭を彩る ${d.footprint.w}×${d.footprint.h} の装飾`,
    emoji: d.emoji,
  })),
  {
    id: "wide-watering",
    displayName: "広口ジョウロ",
    type: "toolUpgrade",
    price: 320,
    description: "水やりの演出と範囲を強化",
    emoji: "💦",
  },
  {
    id: "garden-expansion-8",
    displayName: "庭の拡張許可証（小: 8x8）",
    type: "decoration",
    price: 500,
    description: "6×6の庭を8×8へ拡張します。購入後すぐに適用されます。",
    emoji: "🌱",
  },
  {
    id: "garden-expansion-10",
    displayName: "庭の拡張許可証（中: 10x10）",
    type: "decoration",
    price: 1200,
    description: "8×8の庭を10×10へ拡張します。小サイズへの拡張後に購入できます。",
    emoji: "🏡",
  },
  {
    id: "garden-expansion-12",
    displayName: "庭の拡張許可証（大: 12x12）",
    type: "decoration",
    price: 3000,
    description: "10×10の庭を12×12へ拡張します。中サイズへの拡張後に購入できます。",
    emoji: "🏰",
  },
];
export class Game {
  readonly canvas: HTMLCanvasElement;
  readonly camera: Camera;
  readonly particles: ParticleSystem;
  readonly renderer: IsometricRenderer;
  readonly windShader = WindShader;
  readonly storage: GardenStorage;
  readonly time: TimeManager;
  readonly weather: WeatherSystem;
  readonly seasons: SeasonSystem;
  readonly economy: EconomySystem;
  readonly quests: QuestSystem;
  readonly audio: AudioSystem;
  readonly care: CareSystem;
  readonly photoMode: PhotoMode;
  readonly mascots: MascotSystem;
  readonly grid: GardenGrid;
  readonly plants: PlantState[];
  private hud: HUD;
  private toolbar: Toolbar;
  private shop: ShopModal;
  private detail: DetailModal;
  private questModal: QuestModal;
  private photoModal: PhotoModal;
  private helpModal: HelpModal;
  private mascotModal: MascotModal;
  private onboarding: OnboardingModal;
  private seedDrawer: SeedDrawerModal;
  private decoPanel!: DecorationPanel;
  private inspector: Inspector;
  private notifications: Toast;
  private frame = 0;
  private last = 0;
  private accumulator = 0;
  private selected: ToolbarTool = "water";
  private selectedSeed = "tulip";
  private selectedDecoration: string | null = null;
  private dragging = false;
  private moved = false;
  private down = { x: 0, y: 0 };
  private saveTimer = 0;
  private settings: GameSettings;
  private cleanup: () => void;
  /** 庭の同一性。保存のたびに振り直すとサーバー側の突き合わせが壊れるため、一度決めたら変えない。 */
  private readonly gardenId: string;
  private readonly createdAt: number;
  private readonly sync: SyncManager | null;
  private accountModal: AccountModal | null = null;
  /** ローカル保存の失敗を通知するのは 1 度だけにする。 */
  private storageWarned = false;
  constructor(root: HTMLElement, loaded: SaveData | null = null, services: GameServices | null = null) {
    root.classList.add("game-shell");
    this.sync = services?.sync ?? null;
    this.gardenId = loaded?.gardenId ?? createGardenId();
    this.createdAt = loaded?.createdAt ?? Date.now();
    this.canvas = document.createElement("canvas");
    this.canvas.id = "garden-canvas";
    this.canvas.setAttribute("aria-label", "Garden Meister の庭");
    root.append(this.canvas);
    this.settings = { ...structuredClone(SETTINGS), ...(loaded?.settings ?? {}), daysPerSeason: 10, realMinutesPerGameDay: 3 };
    this.grid = new GardenGrid(
      loaded?.gridWidth ?? INITIAL_GARDEN_SIZE,
      loaded?.gridHeight ?? INITIAL_GARDEN_SIZE,
      loaded?.grid,
    );
    this.plants =
      loaded?.plants?.map((p) => ({
        ...p,
        gridPosition: { ...p.gridPosition },
      })) ?? this.starterPlants();
    this.economy = new EconomySystem(loaded?.inventory, this.grid);
    this.restoreOccupants();
    this.time = new TimeManager(loaded?.time, {
      daysPerSeason: this.settings.daysPerSeason,
      realMinutesPerGameDay: this.settings.realMinutesPerGameDay,
    });
    this.weather = new WeatherSystem(loaded?.weather);
    this.seasons = new SeasonSystem(this.time.snapshot.season);
    this.storage = services?.storage ?? new GardenStorage();
    // 初期サイズを 6×6 にしたため、既に広い庭を持つ既存セーブには対応する許可証を補完しておく。
    for (const size of [8, 10, 12] as const) {
      if (this.grid.width >= size && !this.economy.ownsExpansion(size)) {
        this.economy.buy({ ...SHOP.find((item) => item.id === `garden-expansion-${size}`)!, price: 0 });
      }
    }
    this.quests = new QuestSystem(
      loaded?.quests,
      this.seasons.current,
      this.time.snapshot.totalTicks,
    );
    this.audio = new AudioSystem(this.settings.audio);
    this.camera = new Camera({ tileWidth: TILE_WIDTH, tileHeight: TILE_HEIGHT, minZoom: 0.4, maxZoom: 3 });
    this.particles = new ParticleSystem(
      this.settings.particleQuality === "low" ? 300 : 900,
    );
    const ctx = this.canvas.getContext("2d", { alpha: false });
    if (!ctx) throw new Error("Canvas 2Dを初期化できません。");
    this.renderer = new IsometricRenderer(ctx, this.camera, this.particles);
    this.care = new CareSystem(
      this.grid,
      this.plants,
      this.economy,
      this.quests,
      this.particles,
      this.camera,
      this.audio,
    );
    this.photoMode = new PhotoMode(this.canvas, this.storage);
    this.notifications = new Toast(root);
    this.seasons.subscribe((previous, current) => {
      if (previous === "autumn" && current === "winter") {
        for (const p of this.plants) {
          p.mulched = false;
        }
        this.notifications.show("🌾 冬を迎えました。マルチングで植物を守りましょう！", "info");
      }
    });
    this.mascots = new MascotSystem({
      width: this.grid.width,
      height: this.grid.height,
      getCell: (position) => this.grid.get(position.x, position.y),
      discoverTasks: () => this.discoverMascotTasks(),
      executeCare: (action, position) => this.executeMascotCare(action, position),
      addGP: (amount) => this.economy.addGP(amount),
      restAreas: [{ x: 0, y: 0 }],
      onGrowth: ({ mascotId, levelUp, affinityEmotes }) => {
        const name = getMascotDefinition(mascotId)?.displayName.ja ?? "マスコット";
        this.notifications.success(levelUp ? `${name}がレベルアップしました！` : `${name}となかよくなり、新しいエモートを覚えました！ ${affinityEmotes.join("・")}`);
        this.saveSoon();
      },
    }, loaded ? { ...(loaded.unlockedMascots ? { unlockedMascots: loaded.unlockedMascots } : {}), ...(loaded.activeMascotIds ? { activeMascotIds: loaded.activeMascotIds } : {}), ...(loaded.maxMascotSlots !== undefined ? { maxMascotSlots: loaded.maxMascotSlots } : {}), ...(loaded.mascotStatus ? { mascotStatus: loaded.mascotStatus } : {}), saveVersion: "3.1" } : undefined);
    this.inspector = new Inspector(root);
    this.onboarding = new OnboardingModal(() => {
      localStorage.setItem("gm_onboarding_completed", "true");
      this.notifications.show("庭づくりを始めましょう！ まずはツールを選んでください", "info");
    });
    this.helpModal = new HelpModal(() => this.onboarding.show());
    if (services) {
      this.accountModal = new AccountModal({
        api: services.api,
        sync: services.sync,
        credentials: services.credentials,
        storage: this.storage,
        getSave: () => this.createSave(),
        notify: (message, kind) => this.notifications.show(message, kind ?? "info"),
        onStartNewGarden: () => this.restart(),
        onSwitchGarden: (gardenId) => this.switchGarden(gardenId),
        setTimePaused: (paused) => this.setTimePaused(paused),
        isTimePaused: () => this.timePaused,
        offlineCalendarCapDays: this.time.config.maximumOfflineCalendarDays,
      });
    }
    this.seedDrawer = new SeedDrawerModal(() => this.seedItems(), (id, name) => {
      this.selectedSeed = id;
      this.toolbar.setSelectedSeed(name);
      const plantingForm = getPlantDefinition(id)?.plantingForm;
      const formName = plantingForm === "sapling" ? "苗木" : plantingForm === "seedling" ? "苗" : "種";
      this.notifications.success(`${name}の${formName}を選びました`);
    });
    this.decoPanel = new DecorationPanel(
      root,
      () => this.economy.snapshot.items.filter((i) => i.type === "decoration" && i.quantity > 0),
      (catalogId) => {
        this.selectedDecoration = catalogId;
        this.notifications.show("配置する装飾を選択しました。グリッドをクリックして配置してください。", "info");
      },
      () => {
        if (this.selected === "decorate" && !this.selectedDecoration) {
          this.toolbar.select("water");
        }
      }
    );
    this.hud = new HUD(root, {
      onAudio: () => {
        this.audio.unlock().catch(() => undefined);
        this.settings.audio.muted = this.audio.toggleMuted();
        this.updateUI();
      },
      onQuests: () => this.questModal.show(),
      onShop: () => this.shop.open(),
      onMascots: () => this.mascotModal.show(),
      onPhoto: () => void this.openPhoto(),
      onHelp: () => this.helpModal.show(),
      onAccount: () => this.accountModal ? void this.accountModal.show() : this.notifications.show("この配信ではサーバー同期が無効です。セーブはこのブラウザ内にのみ保存されます。", "info"),
      onToggleSpeed: () => {
        const scales = [1, 2, 5] as const;
        const index = scales.indexOf(this.time.snapshot.timeScale as 1 | 2 | 5);
        const scale = scales[(index + 1) % scales.length] ?? 1;
        this.time.setTimeScale(scale);
        this.notifications.show(`時間の速さを ${scale}x にしました。`, "info");
        this.updateUI();
        this.saveSoon();
      },
      onNap: () => this.nap(),
    });
    this.toolbar = new Toolbar(root, {
      onSelect: (tool) => {
        this.selected = tool;
        if (tool !== "seed") this.seedDrawer.close();
        if (tool === "decorate") {
          this.decoPanel.open();
        } else {
          this.decoPanel.close();
          this.selectedDecoration = null;
        }
      },
      onSeedDrawer: () => this.seedDrawer.open(this.seasons.current),
    });
    this.shop = new ShopModal(this.economy, SHOP, (message) => {
      this.audio.play("purchase");
      this.quests.record({ action: "purchase", season: this.seasons.current });
      if (message) this.notifications.show(message, "success");
      this.saveSoon();
    }, this.mascots, (size) => this.expandGarden(size));
    this.mascotModal = new MascotModal(this.mascots, this.economy, (message) => { this.notifications.show(message, "success"); this.saveSoon() });
    this.detail = new DetailModal();
    this.questModal = new QuestModal(this.quests, () => {
      for (const q of this.quests.all)
        if (q.status === "completed") this.quests.claim(q.id, this.economy);
      this.audio.play("reward");
      this.saveSoon();
    });
    this.photoModal = new PhotoModal(
      this.photoMode,
      () => this.createSave(),
      () => this.seasons.current,
    );
    this.cleanup = this.bindInput();
    this.time.subscribe((e) => {
      if (e.type === "season") {
        this.seasons.transitionTo(e.current);
        this.quests.refresh(e.state.totalTicks, e.current);
        this.toolbar.updateSeason(e.current);
        this.audio.play("reward");
      }
      if (e.type === "day") {
        this.simulateDay();
        this.quests.refresh(e.state.totalTicks, this.seasons.current);
      }
    });
    this.weather.subscribe(() => this.saveSoon());
    this.economy.subscribe(() => this.updateUI());
    this.quests.subscribe(() => this.updateUI());
    this.resize();
    this.centerCamera();
    // 生成直後はレイアウトが確定しておらず canvas の実寸が取れないことがある。
    // 次のフレームで測り直さないと、庭が中央からずれたまま始まる。
    requestAnimationFrame(() => {
      if (!this.canvas.isConnected) return;
      this.resize();
      this.centerCamera();
    });
    this.updateUI();
    window.addEventListener("resize", this.resize);
    document.addEventListener("visibilitychange", this.visibility);
    window.addEventListener("pagehide", this.persist);
    this.reportOfflineProgress(this.time.applyOfflineProgress().progress);
    if (localStorage.getItem("gm_onboarding_completed") !== "true") window.setTimeout(() => this.onboarding.show(), 250);
  }
  start() {
    if (!this.frame) {
      this.last = performance.now();
      this.frame = requestAnimationFrame(this.loop);
    }
  }
  stop() {
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.save();
  }
  destroy() {
    this.stop();
    this.cleanup();
    this.toolbar.destroy();
    this.inspector.destroy();
    this.notifications.destroy();
    window.removeEventListener("resize", this.resize);
    document.removeEventListener("visibilitychange", this.visibility);
    window.removeEventListener("pagehide", this.persist);
    this.sync?.dispose();
    this.audio.dispose();
  }
  /**
   * 新しい庭を始める。いまの庭は削除せずサーバーに残るので、一覧からいつでも戻れる。
   *
   * サーバー側の準備（いまの庭の確定と参照先の切り離し）に失敗したらローカルは一切
   * 触らない。先にローカルだけ消すと、次回起動で以前の庭を拾い直してしまう。
   */
  async restart(): Promise<{ ok: boolean; message?: string }> {
    const prepared = this.sync ? await this.sync.startNewGarden() : { ok: true as const };
    if (!prepared.ok) return prepared;
    this.discardLocalAndReload(true);
    return { ok: true };
  }

  /** 別の庭へ切り替える。 */
  async switchGarden(gardenId: string): Promise<{ ok: boolean; message?: string }> {
    if (!this.sync) return { ok: false, message: "サーバー同期が無効なため、庭を切り替えられません。" };
    const prepared = await this.sync.switchGarden(gardenId);
    if (!prepared.ok) return prepared;
    this.discardLocalAndReload(false);
    return { ok: true };
  }

  /** この庭の時間を止める／進める。設定はセーブに残る。 */
  setTimePaused(paused: boolean) {
    if (paused) this.time.pause();
    else this.time.resume();
    this.save();
    this.updateUI();
  }

  get timePaused(): boolean {
    return this.time.isPaused;
  }

  /**
   * ローカルのセーブを捨てて読み込み直す。
   * オートセーブを止めてから消さないと、離脱時の保存で書き戻ってしまう。
   */
  private discardLocalAndReload(resetTutorial: boolean) {
    this.storage.stashConflict(this.createSave());
    this.teardownWithoutSaving();
    this.storage.clear();
    if (resetTutorial) {
      try {
        localStorage.removeItem("gm_onboarding_completed");
      } catch {
        // 消せなくても続行する。
      }
    }
    window.location.reload();
  }

  /** 保存せずにゲームループと離脱時ハンドラを止める。やり直し・切り替え専用。 */
  private teardownWithoutSaving() {
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.saveTimer = 0;
    document.removeEventListener("visibilitychange", this.visibility);
    window.removeEventListener("pagehide", this.persist);
    this.sync?.dispose();
  }

  /** 起動時の同期結果など、ゲーム外から出したい通知の入口。 */
  notify(message: string, kind: "success" | "warning" | "error" | "info" = "info") {
    this.notifications.show(message, kind);
  }
  createSave(): SaveData {
    const mascotSave = this.mascots.serialize();
    return {
      schemaVersion: 1,
      gardenId: this.gardenId,
      createdAt: this.createdAt,
      updatedAt: Date.now(),
      gridWidth: this.grid.width,
      gridHeight: this.grid.height,
      time: { ...this.time.snapshot },
      weather: { ...this.weather.snapshot },
      grid: this.grid.serialize(),
      plants: this.plants.map((p) => ({
        ...p,
        gridPosition: { ...p.gridPosition },
      })),
      inventory: this.economy.serialize(),
      quests: this.quests.serialize(),
      settings: structuredClone(this.settings),
      unlockedMascots: mascotSave.unlockedMascots,
      activeMascotIds: mascotSave.activeMascotIds,
      maxMascotSlots: mascotSave.maxMascotSlots,
      mascotStatus: mascotSave.mascotStatus,
      saveVersion: "4.0" as never,
      checksum: "",
    };
  }
  save() {
    this.time.markSaved();
    const outcome = this.storage.save(this.createSave());
    this.saveTimer = 0;
    if (outcome.ok) {
      this.sync?.schedulePush();
      return;
    }
    if (!this.storageWarned) {
      this.storageWarned = true;
      this.notifications.show(`${outcome.message} 進行状況が保存されない可能性があります。`, "warning");
    }
  }
  private loop = (now: number) => {
    const dt = Math.min(0.1, (now - this.last) / 1000);
    this.last = now;
    this.accumulator += dt;
    while (this.accumulator >= 1 / 30) {
      this.update(1 / 30);
      this.accumulator -= 1 / 30;
    }
    this.render(now / 1000);
    this.frame = requestAnimationFrame(this.loop);
  };
  private update(dt: number) {
    this.camera.update(dt);
    this.mascots.update(dt);
    const before = this.time.snapshot.totalTicks;
    this.time.update(dt * 1000);
    const elapsed = this.time.snapshot.totalTicks - before;
    if (elapsed > 0) {
      this.weather.evaluate(
        this.time.snapshot.totalTicks,
        this.seasons.current,
      );
      this.grid.update(elapsed, this.seasons.current, this.weather.snapshot);
      for (const p of this.plants) {
        const c = this.grid.get(p.gridPosition.x, p.gridPosition.y);
        if (c) {
          p.soilMoisture = c.soilMoisture;
          p.fertility = c.fertility;
          p.weedPressure = c.weedPressure;
        }
        p.ageTicks += elapsed;
      }
    }
    this.particles.update(dt, {
      season: this.seasons.current,
      weather: this.weather.snapshot,
      width: this.canvas.width,
      height: this.canvas.height,
    });
    this.audio.update(
      dt,
      this.seasons.current,
      this.weather.snapshot.current,
      this.weather.snapshot.windStrength,
    );
    this.saveTimer > 0 && (this.saveTimer -= dt);
    if (this.saveTimer < 0) this.save();
    this.updateUI();
  }
  private render(now: number) {
    const t = this.time.snapshot;
    this.renderer.render({
      cells: this.grid.all,
      plants: this.plants,
      plantTypes: PLANT_DEFINITIONS,
      decorations: this.economy.snapshot.decorations,
      mascots: this.mascots.activeMascots,
      season: this.seasons.current,
      weather: this.weather.snapshot,
      minuteOfDay: t.minuteOfDay,
      time: now,
    });
  }
  private act(x: number, y: number, detail = false) {
    const rect = this.canvas.getBoundingClientRect(),
      sx = (x - rect.left) * (this.canvas.width / rect.width),
      sy = (y - rect.top) * (this.canvas.height / rect.height),
      hit = this.camera.hitTest(
        sx,
        sy,
        (gx, gy) => this.grid.get(gx, gy)?.elevation,
      );
    if (!hit) return;
    const clickedMascot = this.mascots.activeMascots.map((mascot) => ({ mascot, distance: Math.hypot(mascot.position.x - hit.x, mascot.position.y - hit.y) })).sort((a, b) => a.distance - b.distance)[0];
    if (clickedMascot && clickedMascot.distance < .65) { this.mascotModal.show(clickedMascot.mascot.id); return }
    if (detail) {
      const cell = this.grid.get(hit.x, hit.y);
      const plantId = cell?.occupant?.kind === "plant" ? cell.occupant.plantInstanceId : undefined;
      const p = plantId ? this.plants.find((candidate) => candidate.instanceId === plantId) : undefined;
      const t = p && getPlantDefinition(p.speciesId);
      if (p && t) {
        this.camera.focusGrid(
          hit.x,
          hit.y,
          this.canvas.width,
          this.canvas.height,
          2.5,
        );
        this.detail.showPlant(p, t, () =>
          this.care.execute("pest", hit, {
            season: this.seasons.current,
            tick: this.time.snapshot.totalTicks,
          }),
        );
      }
      return;
    }
    if (this.selected === "decorate") {
      if (this.selectedDecoration) {
        const ok = this.economy.placeDecoration(this.selectedDecoration, { x: hit.x, y: hit.y });
        if (ok) {
          this.notifications.success("装飾を配置しました");
          this.selectedDecoration = null;
          this.decoPanel.clearSelection();
          this.saveSoon();
        } else {
          this.notifications.show("この場所には配置できないか、装飾の在庫がありません。", "warning");
          this.decoPanel.open();
        }
      } else {
        this.decoPanel.open();
      }
      return;
    }
    if (this.selected === "mascot") {
      const action = this.mascotActionAt(hit.x, hit.y);
      if (!action) { this.notifications.show("このセルにはお願いできるお世話がありません。", "warning"); return }
      const assigned = this.mascots.requestCare(action, hit);
      this.notifications.show(assigned ? `${getMascotDefinition(assigned)?.displayName.ja ?? "マスコット"}にお願いしました！` : "今お願いできるマスコットがいません。", assigned ? "success" : "warning");
      if (assigned) this.saveSoon();
      return;
    }
    const r = this.care.execute(
      this.selected as CareCommand,
      { x: hit.x, y: hit.y },
      {
        speciesId: this.selectedSeed,
        season: this.seasons.current,
        tick: this.time.snapshot.totalTicks,
      },
    );
    const cell = this.grid.get(hit.x, hit.y);
    const plantId = cell?.occupant?.kind === "plant" ? cell.occupant.plantInstanceId : undefined;
    const plant = plantId ? this.plants.find((candidate) => candidate.instanceId === plantId) : undefined;
    const message = r.success && this.selected === "water" && cell ? `水やりしました！ (水分 ${Math.round(cell.soilMoisture)}%)` : r.success && this.selected !== "remove" && r.gpAwarded > 0 ? `${r.message} (+${r.gpAwarded}GP)` : !r.success && this.selected === "seed" && cell?.hasWeeds ? "雑草を抜いてから植えてください" : !r.success && this.selected === "water" && cell && cell.soilMoisture >= 98 ? "このセルの土壌はすでに十分な水分があります" : !r.success && this.selected === "harvest" && plant?.stage !== "fruiting" ? "まだ収穫できません。実るまで見守りましょう" : r.message;
    this.notifications.show(message, r.success ? "success" : "warning");
    if (r.success) this.saveSoon();
  }
  private mascotActionAt(x: number, y: number): MascotCareAction | null {
    const cell = this.grid.get(x, y);
    if (!cell) return null;
    const plantId = cell.occupant?.kind === "plant" ? cell.occupant.plantInstanceId : undefined;
    const plant = plantId ? this.plants.find((candidate) => candidate.instanceId === plantId) : undefined;
    if (plant?.stage === "fruiting") return "harvesting";
    if (plant && plant.pestLevel > 0) return "pestControl";
    if (cell.hasWeeds || cell.weedPressure > 30) return "weeding";
    if (plant && cell.soilMoisture < 45) return "watering";
    if (plant && !plant.mulched) return "mulching";
    if (cell.fertility < 35) return "fertilizing";
    if (cell.fallenLeaves > 25) return "leafSweeping";
    return null;
  }
  private discoverMascotTasks() {
    const tasks: Array<{ position: { x: number; y: number }; action: MascotCareAction }> = [];
    for (const cell of this.grid.all) { const action = this.mascotActionAt(cell.x, cell.y); if (action) tasks.push({ position: { x: cell.x, y: cell.y }, action }) }
    return tasks;
  }
  private executeMascotCare(action: MascotCareAction, position: { x: number; y: number }) {
    const command: Partial<Record<MascotCareAction, CareCommand>> = { watering: "water", weeding: "weed", pestControl: "pest", fertilizing: "fertilize", pruning: "prune", mulching: "mulch", winterProtection: "winterCover", harvesting: "harvest", leafSweeping: "sweepLeaves" };
    const careCommand = command[action];
    if (!careCommand) return { success: false, gpAwarded: 0 };
    const result = this.care.execute(careCommand, position, { season: this.seasons.current, tick: this.time.snapshot.totalTicks });
    if (result.success) this.saveSoon();
    return { success: result.success, gpAwarded: result.gpAwarded };
  }
  private bindInput() {
    const wheel = (e: WheelEvent) => this.camera.onWheel(e),
      down = (e: PointerEvent) => {
        void this.audio.unlock();
        this.canvas.setPointerCapture(e.pointerId);
        this.dragging = true;
        this.moved = false;
        this.down = { x: e.clientX, y: e.clientY };
        this.camera.onPointerDown(e);
      },
      move = (e: PointerEvent) => {
        if (Math.hypot(e.clientX - this.down.x, e.clientY - this.down.y) > 7)
          this.moved = true;
        this.camera.onPointerMove(e, this.canvas.getBoundingClientRect());
        if (!this.dragging || !this.moved) this.inspectAt(e.clientX, e.clientY);
      },
      up = (e: PointerEvent) => {
        this.camera.onPointerUp(e);
        if (this.dragging && !this.moved)
          this.act(e.clientX, e.clientY, e.detail > 1);
        this.dragging = false;
      };
    this.canvas.addEventListener("wheel", wheel, { passive: false });
    this.canvas.addEventListener("pointerdown", down);
    this.canvas.addEventListener("pointermove", move);
    this.canvas.addEventListener("pointerup", up);
    this.canvas.addEventListener("pointercancel", up);
    this.canvas.addEventListener("pointerleave", this.hideInspector);
    return () => {
      this.canvas.removeEventListener("wheel", wheel);
      this.canvas.removeEventListener("pointerdown", down);
      this.canvas.removeEventListener("pointermove", move);
      this.canvas.removeEventListener("pointerup", up);
      this.canvas.removeEventListener("pointercancel", up);
      this.canvas.removeEventListener("pointerleave", this.hideInspector);
    };
  }
  private resize = () => {
    const dpr = Math.min(2, devicePixelRatio || 1),
      r = this.canvas.getBoundingClientRect();
    this.canvas.width = Math.max(1, Math.round(r.width * dpr));
    this.canvas.height = Math.max(1, Math.round(r.height * dpr));
  };
  private centerCamera() {
    // 庭全体が画面に収まる倍率へ合わせる。倍率が 1.35 固定だったため、6×6 でも
    // 左右が画面外にはみ出し、初期状態で庭を見渡せなかった。
    const halfTiles = this.grid.width + this.grid.height;
    const spanX = halfTiles * TILE_WIDTH * 0.5;
    const spanY = halfTiles * TILE_HEIGHT * 0.5;
    // 縦はツールバーと HUD に隠れるぶん、余白を多めに取る。
    const fit = Math.min((this.canvas.width * 0.9) / spanX, (this.canvas.height * 0.7) / spanY);
    this.camera.setZoomAt(fit, this.canvas.width * 0.5, this.canvas.height * 0.45, true);
    this.camera.panX = this.canvas.width * 0.5;
    this.camera.panY = this.canvas.height * 0.2;
  }
  private expandGarden(size: 8 | 10 | 12) {
    // 庭は 6→8→10→12 と 1 段階ずつ広がる。順番を飛ばした購入は適用しない。
    if (this.grid.width !== size - 2) {
      if (this.grid.width < size - 2) this.notifications.show(`先に${size - 2}×${size - 2}への拡張許可証が必要です。`, "warning");
      return;
    }
    const { offsetX, offsetY } = this.grid.expand(size);
    for (const plant of this.plants) {
      plant.gridPosition.x += offsetX;
      plant.gridPosition.y += offsetY;
    }
    for (const decoration of this.economy.snapshot.decorations) {
      decoration.gridPosition.x += offsetX;
      decoration.gridPosition.y += offsetY;
    }
    for (const mascot of this.mascots.activeMascots) {
      mascot.position.x += offsetX;
      mascot.position.y += offsetY;
      if (mascot.target) { mascot.target.x += offsetX; mascot.target.y += offsetY; }
    }
    this.centerCamera();
    this.camera.setZoomAt(1.35, this.canvas.width / 2, this.canvas.height / 2, true);
    this.notifications.success(`庭が${size}×${size}に広がりました！`);
    this.saveSoon();
  }
  private nap() {
    const before = this.time.snapshot.totalTicks;
    this.time.skipToNextMorning();
    const elapsed = this.time.snapshot.totalTicks - before;
    this.weather.evaluate(this.time.snapshot.totalTicks, this.seasons.current);
    this.grid.update(elapsed, this.seasons.current, this.weather.snapshot);
    for (const plant of this.plants) plant.ageTicks += elapsed;
    const ready = this.plants.filter((plant) => plant.stage === "fruiting").length;
    this.notifications.success(`🌙 ぐっすり眠りました。翌朝6時です${ready ? `。収穫できる植物が${ready}株あります` : ""}。`);
    this.updateUI();
    this.saveSoon();
  }
  private updateUI() {
    const cared = this.plants.reduce((n, p) => n + p.harvestCount, 0);
    this.hud.update({
      time: this.time.snapshot,
      weather: this.weather.snapshot,
      gp: this.economy.snapshot.greenPoints,
      level:
        1 + Math.floor((this.economy.snapshot.greenPoints + cared * 50) / 250),
      muted: this.settings.audio.muted,
    });
  }
  private simulateDay() {
    const seasonEnv = this.seasons.environment;
    const infested: string[] = [];
    for (const p of this.plants) {
      const d = getPlantDefinition(p.speciesId),
        c = this.grid.get(p.gridPosition.x, p.gridPosition.y);
      if (!d || !c) continue;
      c.fertility = Math.max(10, Math.round(c.fertility - 3));
      const prevStage = p.stage;
      const good =
        c.soilMoisture >= d.idealMoisture[0] &&
        c.soilMoisture <= d.idealMoisture[1] &&
        p.health > 35;
      let healthDelta = (good ? 3 : -6) - p.pestLevel * 0.03 - c.weedPressure * 0.025;
      if (c.soilMoisture > d.idealMoisture[1] + 10) healthDelta -= 4;
      if (c.fertility > 90) healthDelta -= 3;
      p.health = Math.max(0, Math.min(100, p.health + healthDelta));

      const inPreferredSeason = (d.preferredSeasons as readonly string[]).includes(this.seasons.current);
      const inAvailableSeason = (d.seasonsAvailable as readonly string[]).includes(this.seasons.current);
      if (!inAvailableSeason && p.stage !== "withered" && p.stage !== "dormant" && this.seasons.current !== "winter") {
        // 1 季節では育ちきらない植物が季節をまたいだだけで枯れていたため緩和する。
        const wiltDamage = p.mulched ? -1.5 : -4;
        p.health = Math.max(0, p.health + wiltDamage);
      }

      // 春の目覚め。awakenFromDormancy はこれまでどこからも呼ばれておらず、
      // 一度冬に休眠した多年草・樹木は二度と成長せず収穫もできなくなっていた。
      if (this.seasons.current === "spring" && p.stage === "dormant") {
        Object.assign(p, this.seasons.awakenFromDormancy(p));
      }

      if (this.seasons.current === "winter" && p.stage !== "withered") {
        const r = this.seasons.evaluateWintering(
          p,
          d,
          this.seasons.getTemperature(
            this.weather.snapshot.temperatureModifier,
          ),
          1,
        );
        Object.assign(p, r.plant);
      }

      // Wither check
      if (p.stage !== "withered" && p.stage !== "dormant") {
        if (p.health <= 0 || (p.health <= 15 && Math.random() < 0.45) || (!inAvailableSeason && p.health <= 20)) {
          p.stage = "withered";
        }
      }
      if (prevStage !== "withered" && p.stage === "withered") {
        this.notifications.show(
          `🥀 ${d.displayName}が健康を損ねて枯れてしまいました…`,
          "warning",
        );
      }

      // 成長。計算は PlantGrowth に集約してあり、ショップの「約N日」表示と同じ式を使う。
      if (p.stage !== "withered" && p.stage !== "dormant") {
        const grown = advanceGrowthDay(d, { stage: p.stage, stageProgress: p.stageProgress }, {
          preferred: inPreferredSeason,
          available: inAvailableSeason,
          wellTended: good,
        });
        p.stage = grown.stage;
        p.stageProgress = grown.stageProgress;
        for (let step = 0; step < grown.stagesAdvanced; step += 1) {
          p.pruneStagePruned = false;
          this.economy.addGP(d.growthGP);
          this.quests.record({ action: "grow", season: this.seasons.current });
        }
      }

      // 害虫の発生。pestResistance は定義されていたが使われておらず、
      // 品種ごとの虫のつきやすさが結果に出ていなかったのでここで効かせる。
      if (p.stage !== "withered" && p.stage !== "dormant") {
        const resistance = Math.max(0.15, 1.25 - d.pestResistance);
        if (Math.random() < seasonEnv.pestSpawnRate * resistance) {
          const before = p.pestLevel;
          p.pestLevel = Math.min(100, p.pestLevel + Math.floor(Math.random() * 8) + 4);
          if (before <= 5 && p.pestLevel > 5) infested.push(d.displayName);
        }
      }
    }
    // 株ごとに通知すると画面が埋まるため、その日ぶんをまとめて 1 件にする。
    if (infested.length > 0) {
      const label = infested.length === 1 ? `${infested[0]}` : `${infested[0]}など${infested.length}株`;
      this.notifications.show(`🐛 ${label}に害虫が発生しました。虫取り網で駆除しましょう`, "warning");
    }
    // Mascot daily maintenance cost
    const activeMascotIds = [...this.mascots.activeMascotIds];
    if (activeMascotIds.length > 0) {
      let totalPaid = 0;
      const deactivatedNames: string[] = [];
      for (const id of activeMascotIds) {
        const wage = this.mascots.getWage(id);
        const def = getMascotDefinition(id);
        const name = def?.displayName.ja ?? id;
        if (this.economy.snapshot.greenPoints >= wage) {
          this.economy.spendGP(wage);
          totalPaid += wage;
        } else {
          if (this.economy.snapshot.greenPoints > 0) {
            totalPaid += this.economy.snapshot.greenPoints;
            this.economy.spendGP(this.economy.snapshot.greenPoints);
          }
          this.mascots.deactivateMascot(id);
          deactivatedNames.push(name);
        }
      }
      if (totalPaid > 0) {
        this.notifications.show(`🐾 マスコットの活動費として ${totalPaid} GP を支払いました。`, "info");
      }
      if (deactivatedNames.length > 0) {
        this.notifications.show(`⚠️ GP不足のため、${deactivatedNames.join("、")}がお休みに入りました。`, "warning");
      }
    }
    this.saveSoon();
  }
  private restoreOccupants() {
    for (const p of this.plants) {
      const d = getPlantDefinition(p.speciesId);
      if (d && !this.grid.get(p.gridPosition.x, p.gridPosition.y)?.occupant)
        this.grid.placePlant(p, d.footprint);
    }
    for (const decoration of this.economy.snapshot.decorations) {
      const definition = DECORATION_DEFINITIONS[decoration.decorationId as keyof typeof DECORATION_DEFINITIONS];
      if (definition && !this.grid.get(decoration.gridPosition.x, decoration.gridPosition.y)?.occupant)
        this.grid.placeDecoration(decoration, definition.footprint);
    }
  }
  private starterPlants(): PlantState[] {
    const data: [string, number, number][] = [
      ["tulip", 1, 2],
      ["herb", 3, 1],
      ["tomato", 2, 4],
    ];
    return data.map(([speciesId, x, y], i) => ({
      instanceId: crypto.randomUUID() as PlantInstanceId,
      speciesId: speciesId as string,
      stage: i === 0 ? "flowering" : "growing",
      stageProgress: 0.45,
      soilMoisture: 60,
      health: 92,
      pestLevel: i === 2 ? 22 : 0,
      weedPressure: 0,
      fertility: 55,
      pollinated: false,
      mulched: false,
      winterProtected: false,
      pruned: false,
      plantedAtTick: 0,
      lastCareTick: 0,
      ageTicks: 0,
      harvestCount: 0,
      coldDamage: 0,
      gridPosition: { x: x as number, y: y as number },
    }));
  }
  private saveSoon() {
    this.saveTimer = 0.8;
  }
  private inspectAt(clientX: number, clientY: number) {
    const rect = this.canvas.getBoundingClientRect();
    const hit = this.camera.hitTest((clientX - rect.left) * (this.canvas.width / rect.width), (clientY - rect.top) * (this.canvas.height / rect.height), (x, y) => this.grid.get(x, y)?.elevation);
    const cell = hit && this.grid.get(hit.x, hit.y);
    if (!cell) { this.inspector.hide(); return; }
    const plantId = cell.occupant?.kind === "plant" ? cell.occupant.plantInstanceId : undefined;
    const plant = plantId ? this.plants.find((candidate) => candidate.instanceId === plantId) : undefined;
    const definition = plant ? getPlantDefinition(plant.speciesId) : undefined;
    this.inspector.show({ cell, weather: this.weather.snapshot, season: this.seasons.current, ...(plant ? { plant } : {}), ...(definition ? { definition } : {}) }, clientX, clientY);
  }
  private hideInspector = () => this.inspector.hide();
  private seedItems(): SeedDrawerItem[] {
    return PLANT_DEFINITION_LIST.map((plant) => ({ id: plant.id, name: plant.id === "herb" ? "ミント" : plant.displayName, icon: PLANT_ICONS[plant.id] ?? "🌱", count: this.economy.snapshot.seeds[plant.id] ?? 0, days: (plant.seasonsAvailable as readonly string[]).includes(this.seasons.current) ? daysToHarvestFrom(plant, this.settings.daysPerSeason, this.seasons.current) : estimateDaysToHarvest(plant, this.settings.daysPerSeason), sellGP: plant.harvestGP, description: plant.plantingForm === "sapling" ? "庭のシンボルとなる果樹の苗木" : plant.plantingForm === "seedling" && (plant.category === "shrub" || plant.category === "herb") ? "庭を華やかに彩る低木・ハーブの苗" : plant.category === "vegetable" ? "食卓を彩る育てやすい野菜" : plant.category === "herb" ? "香りを楽しめる丈夫なハーブ" : plant.category === "shrub" || plant.category === "tree" ? "庭を彩る低木・樹木" : "季節の庭を彩る花", seasonsAvailable: plant.seasonsAvailable, preferredSeasons: plant.preferredSeasons }));
  }
  private openPhoto = async () => {
    this.time.suspend();
    this.hud.setHidden(true);
    this.toolbar.setHidden(true);
    await this.photoModal.show();
    this.photoModal.element.addEventListener(
      "close",
      () => {
        this.time.unsuspend();
        this.hud.setHidden(false);
        this.toolbar.setHidden(false);
      },
      { once: true },
    );
  };
  private visibility = () => {
    if (document.hidden) this.persist();
  };
  /** タブが隠れた/離脱するタイミングでローカル保存し、未送信分をサーバーへ送り切る。 */
  private persist = () => {
    this.save();
    void this.sync?.flush(true);
  };
  private reportOfflineProgress(progress: OfflineProgress) {
    // 実 3 分でゲーム 1 日進むため、少し離席しただけでも日数は動く。
    // 毎回のリロードで通知が出ないよう、実時間で 10 分以上空いたときだけ知らせる。
    if (progress.elapsedDays <= 0 || progress.appliedRealMilliseconds < 10 * 60_000) return;
    const skipped = progress.elapsedDays - progress.simulatedDays;
    const base = `おかえりなさい。${progress.elapsedDays}日ぶんの季節がめぐりました。`;
    const detail = skipped > 0 ? `庭のお世話は${progress.simulatedDays}日ぶんだけ進んでいます。` : "";
    window.setTimeout(() => this.notifications.show(`${base}${detail}`, "info"), 400);
  }
}

/** Game に注入するサーバー同期まわりの依存。未指定ならローカル保存のみで動作する。 */
export interface GameServices {
  sync: SyncManager;
  api: ApiClient;
  credentials: CredentialStore;
  /** main.ts で load() 済みのインスタンスをそのまま使う。locked 状態を共有するため必須。 */
  storage: GardenStorage;
}

/** crypto.randomUUID は安全なコンテキストでのみ使えるため、届かない環境向けの控えを持つ。 */
function createGardenId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

void (null as ToolType | null);
void (null as DecorationInstance | null);
void (null as Season | null);
